import { useState, useCallback, useEffect, useRef } from 'react';
import { calculateTimesheets, getCalcJob } from '../services/timesheetService';

// T6 — puantaj hesabı arka plan işine taşındı: FE, POST /calculate ile kuyruğa yazar ({ jobId }),
// sonra bu aralıkla anketler ve üst sınırda pes eder. Bu davranış önce PuantajPage.handleCalculate
// içindeydi; Plan 3'te Eksik Kayıtlar sayfası da aynı akışı kullandığı için buraya ÇIKARILDI
// (salt refactor — 2sn/5dk/disabled/terminal durumlar birebir korunur).
export const CALC_POLL_INTERVAL_MS = 2000;
export const CALC_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Ortak calculate-job anketleyici. Durumlar (PuantajPage'deki isimlerle birebir):
//   calculating   — enqueue anından (POST calculate) terminal duruma (succeeded/failed/timeout)
//                   kadar true; çağıran taraf butonu/aksiyonları bu süre boyunca disabled tutar.
//   calcFailed    — YALNIZ gerçek terminal başarısızlıkta (job.status === 'failed') set edilir;
//                   çağıran taraf inline mesaj gösterir (bileşen toast'ı YOK — kural).
//   calcErrorKey  — job.error (responseErrors ile aynı çeviri anahtarı).
//   calcTimedOut  — 5dk poll üst sınırı; job DB'de queued/running kalıp sonradan BAŞARIYLA
//                   yazabilir, bu yüzden "başarısız" DAMGASI YANLIŞ olur — nötr "hâlâ sürüyor".
// Poll zamanlayıcısı state DEĞİL ref: her render'da yeniden kurulmaması ve unmount/kapatmada
// temizlenebilmesi için.
export default function usePollCalcJob() {
  const [calculating, setCalculating] = useState(false);
  const [calcFailed, setCalcFailed] = useState(false);
  const [calcErrorKey, setCalcErrorKey] = useState(null);
  const [calcTimedOut, setCalcTimedOut] = useState(false);
  // BAŞARI da artık bir durum: iş başarıyla bittiğinde çağıran taraf bunu EKRANDA gösterebilsin.
  // Önceden başarı yalnız onSucceeded ile "sessizce" haber veriliyordu; Puantaj'da bu, drawer'ın
  // kapanıp hiçbir şeyin değişmemesi anlamına geliyordu (kapsam seçilmemişse takvim zaten veri
  // çekmez) — kullanıcı hesaplamanın çalıştığını hiç göremiyordu. Bileşen toast'ı KURAL GEREĞİ
  // yok (yalnız Axios interceptor toast basar), bu yüzden geri bildirim satır içi durumla verilir.
  const [calcSucceeded, setCalcSucceeded] = useState(false);
  const [calcProcessedCount, setCalcProcessedCount] = useState(0);
  const pollTimerRef = useRef(null);

  const clearCalcPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Unmount'ta bekleyen poll'u temizle (unmounted bileşende setState uyarısı/sızıntı önler).
  useEffect(() => clearCalcPoll, [clearCalcPoll]);

  // Tüm durumları başlangıç değerine döndürür (drawer aç/kapa sırasında kullanılır).
  const resetCalcState = useCallback(() => {
    setCalculating(false);
    setCalcFailed(false);
    setCalcErrorKey(null);
    setCalcTimedOut(false);
    setCalcSucceeded(false);
    setCalcProcessedCount(0);
  }, []);

  // payload'ı kuyruğa yazar ve sonucu anketler. onSucceeded başarıda çağrılır (liste/takvim
  // yenileme gibi çağırana özel yan etkiler oraya konur). Enqueue hatası (400/409/500) ve boş
  // jobId sessizce forma döner — Axios interceptor zaten toast bastı (İKİNCİ mesaj YOK).
  const runCalculate = useCallback(async (payload, { onSucceeded } = {}) => {
    clearCalcPoll(); // savunmacı (M2): önceki poll zinciri her nasılsa hâlâ ayaktaysa önce temizlenir
    setCalculating(true);
    setCalcFailed(false);
    setCalcErrorKey(null);
    setCalcTimedOut(false);
    setCalcSucceeded(false);
    setCalcProcessedCount(0);

    let jobId;
    try {
      const res = await calculateTimesheets(payload);
      jobId = res.data?.data?.jobId;
    } catch {
      // Enqueue hatası — interceptor bu mutasyon için zaten toast bastı; formu geri göster.
      setCalculating(false);
      return;
    }

    if (!jobId) {
      setCalculating(false);
      return;
    }

    const deadline = Date.now() + CALC_POLL_TIMEOUT_MS;

    const poll = async () => {
      let job = null;
      try {
        const r = await getCalcJob(jobId);
        if (r.data?.status) job = r.data.data;
      } catch {
        job = null; // transient — bir sonraki tick'te tekrar denenir (skipErrorToast: sessiz)
      }

      if (job?.status === 'succeeded') {
        setCalculating(false);
        setCalcSucceeded(true);
        setCalcProcessedCount(job.processedCount ?? 0);
        onSucceeded?.(job);
        return;
      }

      if (job?.status === 'failed') {
        setCalculating(false);
        setCalcFailed(true);
        setCalcErrorKey(job.error || null);
        return;
      }

      if (Date.now() >= deadline) {
        // 5 dk üst sınır — job DB'de queued/running kalmaya devam ediyor olabilir ve sonradan
        // BAŞARIYLA yazabilir (backend'de bayat "running" işler için 2 saatlik eşik var). Bunu
        // 'failed' GÖSTERMEK YANLIŞ olurdu — ayrı, nötr calcTimedOut durumu set edilir.
        setCalculating(false);
        setCalcTimedOut(true);
        return;
      }

      pollTimerRef.current = setTimeout(poll, CALC_POLL_INTERVAL_MS);
    };

    pollTimerRef.current = setTimeout(poll, CALC_POLL_INTERVAL_MS);
  }, [clearCalcPoll]);

  return {
    calculating,
    calcFailed,
    calcErrorKey,
    calcTimedOut,
    calcSucceeded,
    calcProcessedCount,
    clearCalcPoll,
    resetCalcState,
    runCalculate,
  };
}
